import { queryOptions } from '@tanstack/react-query';
import { apiClient } from '@/api/axios';
import { API } from '@/api/endpoints';
import {
  pcmpAccountsPaymentResultSchema,
  pcmpAccountsReturnResultSchema,
  pcmpAdminRejectResultSchema,
  pcmpDisbursementConfirmResultSchema,
  pcmpHoldResultSchema,
  pcmpMisDecisionResultSchema,
  pcmpMktDecisionResultSchema,
  pcmpMktDisbursementResultSchema,
  pcmpMktExplanationResultSchema,
  pcmpMktReturnResultSchema,
  pcmpProposalDetailSchema,
  pcmpProposalEditResultSchema,
  type PcmpAccountsPaymentResult,
  type PcmpAccountsReasonPayload,
  type PcmpAdminRejectPayload,
  type PcmpAdminRejectResult,
  type PcmpDisbursementConfirmPayload,
  type PcmpDisbursementConfirmResult,
  type PcmpHoldPayload,
  type PcmpHoldResult,
  type PcmpMisDecisionPayload,
  type PcmpMisDecisionResult,
  type PcmpMktApprovePayload,
  type PcmpMktDecisionResult,
  type PcmpMktDisbursementPayload,
  type PcmpMktDisbursementResult,
  type PcmpMktExplanationPayload,
  type PcmpMktExplanationResult,
  type PcmpMktRejectPayload,
  type PcmpPaymentSourcePayload,
  type PcmpProposalDetail,
  type PcmpProposalEditPayloads,
  type PcmpProposalEditResult,
  type PcmpProposalEditSection,
  type PcmpReleasePayload,
  type PcmpReturnPaymentPayload,
  type PcmpReturnPaymentResult,
} from '../types/pcmpProposal.types';
import {
  pcmpCorrectionApprovalResultSchema,
  type PcmpCorrectionApprovalResult,
  type PcmpCorrectionApprovePayload,
  type PcmpCorrectionPaymentPayload,
} from '../types/pcmpCorrection.types';

export const pcmpProposalQueryKeys = {
  all: () => ['pcmp-proposals'] as const,
  details: () => [...pcmpProposalQueryKeys.all(), 'detail'] as const,
  detail: (id: number) => [...pcmpProposalQueryKeys.details(), id] as const,
};

// Always fresh: approvals and edits by other users move a proposal on often.
export const pcmpProposalDetailQueryOptions = (id: number) =>
  queryOptions({
    queryKey: pcmpProposalQueryKeys.detail(id),
    queryFn: async (): Promise<PcmpProposalDetail> => {
      const res = await apiClient.get(API.pcmpProposal.detail(id));
      return pcmpProposalDetailSchema.parse(res.data);
    },
    staleTime: 0,
  });

export async function editPcmpProposalSection<S extends PcmpProposalEditSection>(
  id: number,
  section: S,
  payload: PcmpProposalEditPayloads[S],
): Promise<PcmpProposalEditResult> {
  const res = await apiClient.patch(API.pcmpProposal.editSection(id, section), payload);
  return pcmpProposalEditResultSchema.parse(res.data);
}

export async function changePcmpProposalPaymentSource(
  id: number,
  payload: PcmpPaymentSourcePayload,
): Promise<PcmpProposalEditResult> {
  const res = await apiClient.patch(API.pcmpProposal.paymentSource(id), payload);
  return pcmpProposalEditResultSchema.parse(res.data);
}

export async function approvePcmpProposalMarketing(
  id: number,
  payload: PcmpMktApprovePayload,
): Promise<PcmpMktDecisionResult> {
  const res = await apiClient.post(API.pcmpProposal.marketingApprove(id), payload);
  return pcmpMktDecisionResultSchema.parse(res.data);
}

export async function rejectPcmpProposalMarketing(
  id: number,
  payload: PcmpMktRejectPayload,
): Promise<PcmpMktDecisionResult> {
  const res = await apiClient.post(API.pcmpProposal.marketingReject(id), payload);
  return pcmpMktDecisionResultSchema.parse(res.data);
}

export async function submitPcmpMarketingExplanation(
  id: number,
  payload: PcmpMktExplanationPayload,
): Promise<PcmpMktExplanationResult> {
  const res = await apiClient.post(API.pcmpProposal.marketingExplanation(id), payload);
  return pcmpMktExplanationResultSchema.parse(res.data);
}

export async function decidePcmpProposalMis(
  id: number,
  payload: PcmpMisDecisionPayload,
): Promise<PcmpMisDecisionResult> {
  const res = await apiClient.post(API.pcmpProposal.misDecision(id), payload);
  return pcmpMisDecisionResultSchema.parse(res.data);
}

/** Marketing channel payout: records the uploaded proof as disbursed. */
export async function mktDisbursePcmpProposal(
  id: number,
  payload: PcmpMktDisbursementPayload,
): Promise<PcmpMktDisbursementResult> {
  const res = await apiClient.post(API.pcmpProposal.marketingDisbursement(id), payload);
  return pcmpMktDisbursementResultSchema.parse(res.data);
}

/** Disbursement Failed, before or after payout (the API derives the stage). */
export async function failPcmpProposalAccounts(
  id: number,
  payload: PcmpAccountsReasonPayload,
): Promise<PcmpAccountsPaymentResult> {
  const res = await apiClient.post(API.pcmpProposal.accountsDisbursementFailed(id), payload);
  return pcmpAccountsPaymentResultSchema.parse(res.data);
}

/**
 * Admin / MIS: an Accounts payout came back, all or part (LDS-765). The
 * return that completes the amount closes the proposal for good.
 */
export async function returnPcmpProposalAccounts(
  id: number,
  payload: PcmpReturnPaymentPayload,
): Promise<PcmpReturnPaymentResult> {
  const res = await apiClient.post(API.pcmpProposal.accountsReturn(id), payload);
  return pcmpAccountsReturnResultSchema.parse(res.data);
}

/** Admin / MIS: a Marketing payout came back, all or part (LDS-766). */
export async function returnPcmpMarketingPayment(
  id: number,
  payload: PcmpReturnPaymentPayload,
): Promise<PcmpReturnPaymentResult> {
  const res = await apiClient.post(API.pcmpProposal.marketingReturn(id), payload);
  return pcmpMktReturnResultSchema.parse(res.data);
}

/** The submitting MO confirms the Accounts payout. Final (LDS-747). */
export async function confirmPcmpProposalDisbursement(
  id: number,
  payload: PcmpDisbursementConfirmPayload,
): Promise<PcmpDisbursementConfirmResult> {
  const res = await apiClient.post(API.pcmpProposal.disbursementConfirm(id), payload);
  return pcmpDisbursementConfirmResultSchema.parse(res.data);
}

/** Pauses the proposal at its current stage; its statuses stay as they are. */
export async function holdPcmpProposal(
  id: number,
  payload: PcmpHoldPayload,
): Promise<PcmpHoldResult> {
  const res = await apiClient.post(API.pcmpProposal.hold(id), payload);
  return pcmpHoldResultSchema.parse(res.data);
}

/** Lifts the hold; the proposal resumes where it stopped. */
export async function releasePcmpProposal(
  id: number,
  payload: PcmpReleasePayload,
): Promise<PcmpHoldResult> {
  const res = await apiClient.post(API.pcmpProposal.release(id), payload);
  return pcmpHoldResultSchema.parse(res.data);
}

/** Admin reject: closes the proposal for good, pulling it out of any batch. */
export async function adminRejectPcmpProposal(
  id: number,
  payload: PcmpAdminRejectPayload,
): Promise<PcmpAdminRejectResult> {
  const res = await apiClient.post(API.pcmpProposal.adminReject(id), payload);
  return pcmpAdminRejectResultSchema.parse(res.data);
}

/** Correction: the failed payment's corrected bank or wallet details (a new account row). */
export async function correctPcmpProposalPayment(
  id: number,
  payload: PcmpCorrectionPaymentPayload,
): Promise<PcmpProposalEditResult> {
  const res = await apiClient.patch(API.pcmpProposal.correctionPayment(id), payload);
  return pcmpProposalEditResultSchema.parse(res.data);
}

/** Correction approved: Corrected, waiting on the Accounts Reverified tab. */
export async function approvePcmpProposalCorrection(
  id: number,
  payload: PcmpCorrectionApprovePayload,
): Promise<PcmpCorrectionApprovalResult> {
  const res = await apiClient.post(API.pcmpProposal.correctionApprove(id), payload);
  return pcmpCorrectionApprovalResultSchema.parse(res.data);
}
